/**
 * How each searchable record type is labelled and where it opens.
 *
 * @const {!Object<string, {label: string, url: function((string|number)): string}>}
 */
export const RECORD_TYPES = {
  sales_order: { label: "Sales order", url: id => `/sales/order/${id}` },
  purchase_order: { label: "Purchase order", url: id => `/orders/purchase/${id}` },
  transfer_order: { label: "Transfer order", url: id => `/transfer_order/${id}` },
  shipment: { label: "Shipment", url: id => `/shipment/po-to/${id}` },
  sale_invoice: { label: "Invoice", url: id => `/sale_invoice/${id}` },
  customer: { label: "Customer", url: id => `/master/customer/update/${id}` },
  supplier: { label: "Supplier", url: id => `/master/supplier/update/${id}` },
  product: { label: "Product", url: id => `/master/product/update/${id}` },
};

/** @const {number} */
export const MIN_RECORD_QUERY = 2;

/** @const {number} */
const MAX_RECENT = 5;

/**
 * Turns an API search hit into a Quick Jump row, or null for unknown types.
 *
 * @param {{type: string, id: (string|number), title: string, subtitle: ?string, status: ?string}} hit
 * @return {?{key: string, type: string, id: *, title: string, meta: string, url: string}}
 */
export const toRecordRow = hit => {
  const config = RECORD_TYPES[hit?.type];
  if (!config) return null;
  return {
    key: `${hit.type}:${hit.id}`,
    type: hit.type,
    id: hit.id,
    title: hit.title,
    subtitle: hit.subtitle || "",
    meta: config.label,
    url: config.url(hit.id),
  };
};

const recentKey = (userId, companyId) =>
  `quickJumpRecent:${userId ?? "anon"}:${companyId ?? "none"}`;

/**
 * Records recently opened from Quick Jump, newest first.
 *
 * @param {*} userId
 * @param {*} companyId
 * @return {!Array<!Object>}
 */
export const readRecentRecords = (userId, companyId) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(recentKey(userId, companyId)) || "[]");
    return Array.isArray(stored) ? stored.filter(row => RECORD_TYPES[row?.type]) : [];
  } catch {
    return [];
  }
};

/**
 * @param {*} userId
 * @param {*} companyId
 * @param {!Object} row A row from `toRecordRow`.
 */
export const rememberRecentRecord = (userId, companyId, row) => {
  try {
    const next = [
      row,
      ...readRecentRecords(userId, companyId).filter(item => item.key !== row.key),
    ];
    window.localStorage.setItem(
      recentKey(userId, companyId),
      JSON.stringify(next.slice(0, MAX_RECENT)),
    );
  } catch {
    // Private mode or full storage: Recent is a nicety, not required.
  }
};
